import { useAuth0 } from '@auth0/auth0-react';
import { deleteObject, getBytes, getStorage, ref, uploadBytes } from 'firebase/storage';
import { firebaseApp } from 'firebaseConfig';
import { useCallback } from 'react';
import API from 'api/API';
import { useForceStoragePassthroughForLocation } from 'api/hooks/useForceStoragePassthroughForLocation';
import useForceStoragePassthrough from 'hooks/useForceStoragePassthrough';

export type PinKeyPathParams = {
  tenantId: string;
  locationId: string;
  userId: string;
};

const GS_PREFIX = 'gs://';

// the env var may or may not carry the gs:// scheme; paths are always handled as gs:// URLs
const toGsUrl = (path: string): string => GS_PREFIX + path.replace(GS_PREFIX, '');

// client-encrypted PIN keystore ({tenant}/{location}/{user}/pin.key), enforced by
// keystore.rules (owner write/delete, owner-or-co-located read). Mirrors googleStorage.helpers.ts.
const useKeystoreStorageHelpers = () => {
  const { getAccessTokenSilently } = useAuth0();

  const { forceStoragePassthroughForLocation } = useForceStoragePassthroughForLocation();
  const forceStoragePassthrough = useForceStoragePassthrough()[0];
  const gcpNetworkBlocked =
    forceStoragePassthroughForLocation || forceStoragePassthrough || !!localStorage.getItem('gcpNetworkBlocked');

  const constructPinKeyPath = useCallback(({ tenantId, locationId, userId }: PinKeyPathParams): string => {
    return toGsUrl(`${process.env.REACT_APP_FIREBASE_KEYSTORE_BUCKET}/${tenantId}/${locationId}/${userId}/pin.key`);
  }, []);

  const uploadPinKey = useCallback(
    async (pinKeyPath: string, ciphertext: string): Promise<void> => {
      if (gcpNetworkBlocked) {
        await API.users.uploadKeystoreFile(toGsUrl(pinKeyPath), ciphertext);
        return;
      }

      const bytes = new TextEncoder().encode(ciphertext);
      await uploadBytes(ref(getStorage(firebaseApp), pinKeyPath), bytes);
    },
    [gcpNetworkBlocked],
  );

  const getPinKey = useCallback(
    async (pinKeyPath: string): Promise<string> => {
      if (gcpNetworkBlocked) {
        await getAccessTokenSilently();
        const buffer = await API.users.downloadKeystoreFile(toGsUrl(pinKeyPath));
        return new TextDecoder().decode(buffer);
      }

      const bytes = await getBytes(ref(getStorage(firebaseApp), pinKeyPath));
      return new TextDecoder().decode(bytes);
    },
    [gcpNetworkBlocked, getAccessTokenSilently],
  );

  const deletePinKey = useCallback(
    async (pinKeyPath: string): Promise<void> => {
      if (gcpNetworkBlocked) {
        await API.users.deleteKeystoreFile(toGsUrl(pinKeyPath));
        return;
      }

      await deleteObject(ref(getStorage(firebaseApp), pinKeyPath));
    },
    [gcpNetworkBlocked],
  );

  return {
    constructPinKeyPath,
    uploadPinKey,
    getPinKey,
    deletePinKey,
    gcpNetworkBlocked,
  };
};

export default useKeystoreStorageHelpers;
